<template>
    <div class="episodes">
        <show-import-placeholder
            v-if="[ImportStatus.Pending, ImportStatus.Error].includes(show.importStatus)"
            :import-status="show.importStatus"
        />

        <template v-else>
            <episodes-list :show="show" />
            <transition name="fade">
                <add-episode-modal
                    v-if="addEpisodeModal.visible"
                    :show="show"
                    @upload="onUpload"
                    @cancel="onUploadCancel"
                    @store="onModalStore"
                    @update="onModalUpdate"
                    @delete="onModalDelete"
                    @close="onModalClose"
                />
            </transition>
        </template>
    </div>
</template>
<script>
// Extenal dependencies.
import { mapValues } from 'lodash';
import { CancelToken } from 'axios';
import { mapActions, mapState, mapMutations } from 'vuex';
// Internal dependencies.
import AddEpisodeModal from '@/components/Shows/Episodes/AddEpisodeModal';
import EpisodesList from '@/components/Shows/Episodes/EpisodesList';
import ShowImportPlaceholder from '@/components/Shows/ShowImportPlaceholder';
import preventUnload from '@/mixins/preventUnload';
import toasts from '@/mixins/toasts';
import { convertToFormData } from '@/utils/formData';
import episodeModal from '@/utils/episodeModal';
import getAudioDuration from '@/utils/getAudioDuration';
import {
    UPLOAD_STATUS_WAITING,
    UPLOAD_STATUS_INITIALIZING,
    UPLOAD_STATUS_UPLOADING,
    UPLOAD_STATUS_UPLOADED,
    UPLOAD_STATUS_ERROR,
} from '@/utils/config/episodeOptions';
import { ImportStatus } from '@/utils/show';

export default {
    components: {
        AddEpisodeModal,
        EpisodesList,
        ShowImportPlaceholder,
    },

    mixins: [
        preventUnload,
        toasts,
    ],

    props: {
        show: {
            type: [Object, Boolean],
            default: () => ({}),
        },
    },

    data: () => ({
        cancelTokenSource: null,
        unloadMessage: 'Episode upload in progress. Do you really want to exit this page?',
        cancelMessage: 'Do you really want to cancel Episode upload?',
        closeMessage: 'Do you really want to close this form? All changes will be discarded.',
        ImportStatus,
    }),

    computed: {
        ...mapState(['user']),

        ...mapState({
            addEpisodeModal: state => state.UIState.addEpisodeModal,
        }),
    },

    /**
     * Mounts component.
     */
    mounted() {
        if (! this.show.hosted) {
            this.$router.push(`/shows/${this.show.hash}`);
        }
    },

    methods: {
        ...mapMutations(['changeUIState']),

        ...mapActions({
            uploadEpisode: 'episode/uploadEpisode',
            storeEpisode: 'episode/storeEpisode',
            getUploadData: 'episode/getUploadData',
            updateEpisode: 'episode/updateEpisode',
            deleteEpisode: 'episode/deleteEpisode',
        }),

        /**
         * Handles file upload.
         *
         * @param {File}    file      File to upload.
         */
        async onUpload(file) {
            const duration = await getAudioDuration(file);

            episodeModal.setAudioFile(file);
            episodeModal.setAudioFileUploadStatus(UPLOAD_STATUS_INITIALIZING);
            episodeModal.setAudioFileUploadProgress(0);
            episodeModal.updateEpisode({ duration });

            this.preventUnload(true);
            const response = await this.startUpload(file);
            this.preventUnload(false);

            if ('cancelled' === this.addEpisodeModal.uploadStatus) {
                return;
            }

            this.finishUpload(response.url);
        },

        /**
         * Starts file upload.
         *
         * @param   {File}   file Audio file.
         * @returns {object}      Response from upload.
         */
        async startUpload(file) {
            try {
                const uploadData = await this.getUploadData({ hash: this.show.hash, file });

                this.cancelTokenSource = CancelToken.source();
                episodeModal.setAudioFileUploadStatus(UPLOAD_STATUS_UPLOADING);

                const response = await this.uploadEpisode({
                    file,
                    uploadData,
                    options: {
                        onUploadProgress: this.onUploadProgress,
                        cancelToken: this.cancelTokenSource.token,
                    },
                });

                return response;
            } catch (e) {
                episodeModal.setAudioFileUploadStatus(UPLOAD_STATUS_ERROR);
            }

            this.cancelTokenSource = null;

            return {};
        },

        /**
         * Finishes file upload.
         *
         * @param  {string} url [description]
         */
        async finishUpload(url) {
            if (! url) {
                episodeModal.setAudioFileUploadStatus(UPLOAD_STATUS_ERROR);

                return;
            }

            episodeModal.setAudioFileUploadStatus(UPLOAD_STATUS_UPLOADED);
            episodeModal.updateEpisode({ file: url });

            if (episodeModal.isStoreDeferred()) {
                const { status, data } = await this.saveEpisode();
                const prevent = await this.postSaveActions(status, data);

                episodeModal.deferStore(false);

                if (prevent) {
                    episodeModal.updateEpisode({ publishedAt: null });
                    episodeModal.open();

                    return;
                }

                episodeModal.setInitialData();
            }
        },

        /**
         * Handles upload progress event.
         *
         * @param  {Event} event Upload progress event.
         */
        onUploadProgress(event) {
            const uploadPercentage = parseInt(Math.round((event.loaded * 100) / event.total));

            episodeModal.setAudioFileUploadProgress(uploadPercentage);
        },

        /**
         * Cancels file upload if is in progress.
         */
        onUploadCancel() {
            this.cancelUpload();
        },

        /**
         * Closes modal
         */
        onModalClose() {
            if (episodeModal.inCreateContext() && ! episodeModal.isStoreDeferred()) {
                if (! confirm(this.closeMessage)) {
                    return;
                }
            }

            if (! episodeModal.isStoreDeferred()) {
                episodeModal.setInitialData();
            }

            episodeModal.close();
        },

        /**
         * Cancels file upload.
         *
         * @returns {boolean} Cancel result.
         */
        cancelUpload() {
            if (this.cancelTokenSource && confirm(this.cancelMessage)) {
                this.cancelTokenSource.cancel();

                this.cancelCleanup();

                return true;
            }

            return false;
        },

        /**
         * Handles Episode store.
         */
        async onModalStore() {
            const prevent = this.preSaveActions();

            if (prevent) {
                return;
            }

            const { status, data } = await this.saveEpisode();

            await this.postSaveActions(status, data);
        },

        /**
         * Handles Episode update.
         */
        async onModalUpdate() {
            const prevent = this.preSaveActions();

            if (prevent) {
                return;
            }

            const { status, data } = await this.saveEpisode();

            await this.postSaveActions(status, data);
        },

        /**
         * Performs actions before saving episode.
         *
         * @returns {boolean} Information about preventing execution.
         */
        preSaveActions() {
            if (episodeModal.isStoreDeferred()) {
                episodeModal.setSaveStatus(null);
                episodeModal.close();

                this.toastSuccess({
                    message: { title: 'Episode audio is still uploaded. Please wait patiently.' },
                });

                return true;
            }

            if (episodeModal.isAudioFileUploading()) {
                episodeModal.deferStore();
                episodeModal.setSaveStatus(null);
                episodeModal.close();

                this.toastSuccess({
                    message: { title: 'Episode upload will take place in background. Please wait patiently.' },
                });

                return true;
            }

            return false;
        },

        /**
         * Saves episode.
         */
        async saveEpisode() {
            if (! episodeModal.episodeExists()) {
                return await this.storeEpisode({
                    hash: this.show.hash,
                    data: this.prepareEpisodeData(),
                });
            }

            return await this.updateEpisode({
                hash: this.show.hash,
                episodeId: this.addEpisodeModal.episode.id,
                data: this.prepareEpisodeData(),
            });
        },

        /**
         * Performs actions after saving episode.
         *
         * @param  {number} status  Save status.
         * @param  {object} data    Response data.
         * @returns {void}
         */
        async postSaveActions(status, data) {
            let errorMessage = null;

            if (400 <= status) {
                episodeModal.setSaveStatus(null);
                errorMessage = 'An error occured during episode save.';
            }

            if (422 === status) {
                episodeModal.setErrors(
                    mapValues(data.errors, errors => Array.isArray(errors) ? errors[0] : errors)
                );
                errorMessage = data.message;
            }

            if (errorMessage) {
                this.toastError({ message: { title: errorMessage } });

                return true;
            }

            episodeModal.setSaveStatus('saved');

            if (null !== episodeModal.getEpisodeData('publishedAt')) {
                episodeModal.close();
                episodeModal.setInitialData();
            } else {
                episodeModal.updateModalState({ context: 'edit' });
                episodeModal.updateEpisode(data.data);
                setTimeout(() => episodeModal.setSaveStatus(null), 5000);
            }

            this.toastSuccess({ message: { title: 'Episode successfuly saved.' } });

            await this.refreshEpisodesList();

            return false;
        },

        /**
         * Handles Episode delete.
         *
         * @param  {number} episodeId Episode ID.
         */
        async onModalDelete(episodeId) {
            if (! confirm('Do you really want to delete this Episode? This action cannot be undone.')) {
                return;
            }

            const previousStatus = episodeModal.getEpisodeData('status');

            episodeModal.close();
            episodeModal.setInitialData();

            this.updateStoreEpisode(episodeId, { status: 'deleting' });

            const { status } = await this.deleteEpisode({
                hash: this.show.hash,
                episodeId,
            });

            if (200 === status) {
                await this.refreshEpisodesList();
                this.toastSuccess({ message: { title: 'Episode successfuly deleted.' } });

                return;
            }

            this.updateStoreEpisode(episodeId, { status: previousStatus });
            this.toastError({ message: { title: 'An error occured during deleting Episode.' } });
        },

        /**
         * Prepares Episode data.
         *
         * @returns {FormData} Episode data.
         */
        prepareEpisodeData() {
            const episode = { ...this.addEpisodeModal.episode };

            // Do not send artwork if is null (default)
            if (null === episode.artwork) {
                delete episode.artwork;
            }

            // Nullify episode artwork if is set and not instance of File.
            if (episode.artwork && ! (episode.artwork instanceof File)) {
                episode.artwork = null;
            }

            return convertToFormData(episode);
        },

        /**
         * Fires after cancelling request.
         *
         * @see /resources/js/dashboard/mixins/preventUnload.js@beforeRouteLeave
         */
        cancelCleanup() {
            this.preventUnload(false);

            episodeModal.setAudioFileUploadStatus(UPLOAD_STATUS_WAITING);
            episodeModal.setAudioFileUploadProgress(0);
            episodeModal.setAudioFile(null);
        },

        /**
         * Refreshes list of episodes.
         */
        async refreshEpisodesList() {
            await Promise.all([
                this.$store.dispatch('episode/loadEpisodes', { hash: this.show.hash, query: { page: 1 } }),
                this.$store.dispatch('episode/loadEpisodesCount', { hash: this.show.hash, query: { page: 1 } }),
            ]);
        },

        /**
         * Update Episode in store.
         *
         * @param  {number} episodeId Episode ID.
         * @param  {object} data      Episode data to update.
         */
        updateStoreEpisode(episodeId, data) {
            this.$store.dispatch('episode/updateStoreEpisode', { episodeId, data });
        },
    },
};
</script>
